/* Guscio dell'applicazione: barra, navigazione, intestazione di pagina.
 *
 * La navigazione è la stessa lista renderizzata due volte — in fondo per il
 * telefono, in alto per il desktop — e il CSS ne mostra una sola. Un solo
 * elenco significa che aggiungere una schermata non può dimenticarne una delle
 * due forme.
 */

import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Pulsante } from './Base';
import { usaAccesso } from '../auth/ContestoAccesso';

export interface VoceNavigazione {
  percorso: string;
  etichetta: string;
  icona: ReactNode;
}

export const VOCI_NAVIGAZIONE: VoceNavigazione[] = [
  { percorso: '/', etichetta: 'Dashboard', icona: <IcoDashboard /> },
  { percorso: '/backtest', etichetta: 'Backtest', icona: <IcoRicerca /> },
  { percorso: '/gruppi', etichetta: 'Gruppi', icona: <IcoGruppi /> },
  { percorso: '/preferiti', etichetta: 'Preferiti', icona: <IcoStella /> },
  { percorso: '/tradebot', etichetta: 'Tradebot', icona: <IcoBot /> },
  { percorso: '/wallet', etichetta: 'Wallet', icona: <IcoWallet /> },
  { percorso: '/cassaforte', etichetta: 'Cassaforte', icona: <IcoCassaforte /> },
  { percorso: '/impostazioni', etichetta: 'Impostazioni', icona: <IcoIngranaggio /> },
];

export function Guscio() {
  const { sessione, esci } = usaAccesso();

  return (
    <div className="sb-guscio">
      <header className="sb-barra">
        <div className="sb-barra__contenuto">
          <div className="sb-marchio">
            <span className="sb-marchio__segno" aria-hidden="true">
              <IcoMarchio />
            </span>
            <span className="sb-marchio__nome">
              Sem<span className="sb-marchio__teal">Bot</span>
            </span>
          </div>

          <nav className="sb-nav sb-nav--alto" aria-label="Sezioni">
            {VOCI_NAVIGAZIONE.map((v) => (
              <VoceNav key={v.percorso} voce={v} />
            ))}
          </nav>

          <span className="sb-spazio" />

          {sessione && (
            <Pulsante variante="nudo" onClick={esci} title="Esci dalla sessione">
              {sessione.utente.username}
            </Pulsante>
          )}
        </div>
      </header>

      <main className="sb-corpo">
        <Outlet />
      </main>

      <nav className="sb-nav sb-nav--basso" aria-label="Sezioni">
        {VOCI_NAVIGAZIONE.map((v) => (
          <VoceNav key={v.percorso} voce={v} />
        ))}
      </nav>
    </div>
  );
}

function VoceNav({ voce }: { voce: VoceNavigazione }) {
  return (
    <NavLink
      to={voce.percorso}
      end={voce.percorso === '/'}
      className={({ isActive }) => `sb-nav__voce${isActive ? ' sb-nav__voce--attiva' : ''}`}
    >
      <span aria-hidden="true">{voce.icona}</span>
      <span>{voce.etichetta}</span>
    </NavLink>
  );
}

/* ---------- intestazione di pagina ---------- */

export function IntestazionePagina({
  titolo,
  sotto,
  azioni,
}: {
  titolo: string;
  /** Conteggi e contesto: quanti risultati, su che periodo. */
  sotto?: ReactNode;
  azioni?: ReactNode;
}) {
  return (
    <div className="sb-intestazione">
      <div className="sb-intestazione__riga">
        <div style={{ flex: 1, minWidth: 180 }}>
          <h1 className="sb-intestazione__titolo">{titolo}</h1>
          {sotto && <div className="sb-intestazione__sotto">{sotto}</div>}
        </div>
        {azioni}
      </div>
    </div>
  );
}

/* ---------- icone della navigazione ---------- */

function svg(figli: ReactNode, dim = 18) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {figli}
    </svg>
  );
}

/* Il marchio vero, non un segno disegnato a mano. Sta su fondo scuro perche' il nastro
   superiore della S e' bianco e su fondo chiaro sparirebbe: qui il fondo e' sempre scuro. */
function IcoMarchio() {
  return (
    <img
      src="/brand/sembot-mark.png"
      alt=""
      width={22}
      height={22}
      style={{ display: 'block', width: 22, height: 22, borderRadius: 5, flex: '0 0 auto' }}
    />
  );
}

function IcoDashboard() {
  return svg(
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="11" width="7" height="10" rx="1.5" />
      <rect x="3" y="15" width="7" height="6" rx="1.5" />
    </>,
  );
}

function IcoRicerca() {
  return svg(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </>,
  );
}

function IcoGruppi() {
  return svg(
    <>
      <path d="M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3z" />
      <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </>,
  );
}

function IcoStella() {
  return svg(<path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9-5.2-2.8-5.2 2.8 1-5.9-4.3-4.1 5.9-.8z" />);
}

function IcoBot() {
  return svg(
    <>
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 4v4M9 14h.01M15 14h.01" />
    </>,
  );
}

function IcoWallet() {
  return svg(
    <>
      <path d="M3 8a3 3 0 0 1 3-3h12a2 2 0 0 1 2 2v1" />
      <rect x="3" y="7" width="18" height="13" rx="3" />
      <path d="M16 13h2" />
    </>,
  );
}

function IcoCassaforte() {
  return svg(
    <>
      <rect x="4" y="10" width="16" height="10" rx="2.5" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      <path d="M12 14v2" />
    </>,
  );
}

function IcoIngranaggio() {
  return svg(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
    </>,
  );
}
